import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  Position,
  MarkerType,
  Handle,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Loader2, Download, Image } from "lucide-react";
import { Button } from "~/components/ui/button";
import { toast } from "sonner";

interface SchemaTable {
  name: string;
  comment: string | null;
  columns: {
    name: string;
    type: string;
    nullable: boolean;
    hasDefault: boolean;
    isPrimaryKey: boolean;
    isForeignKey: boolean;
  }[];
}

interface SchemaEdge {
  id: string;
  source: string;
  sourceColumn: string;
  target: string;
  targetSchema: string;
  targetColumn: string;
}

interface DiagramData {
  schema: string;
  tables: SchemaTable[];
  edges: SchemaEdge[];
}

// Custom node for table display
function TableNode({ data }: { data: { table: SchemaTable; onSelect: (name: string) => void; onHover: (name: string | null) => void } }) {
  const { table, onSelect, onHover } = data;

  return (
    <div
      className="rounded-lg border border-border bg-card shadow-md min-w-50 max-w-70 cursor-pointer"
      onDoubleClick={() => onSelect(table.name)}
      onMouseEnter={() => onHover(table.name)}
      onMouseLeave={() => onHover(null)}
    >
      <Handle type="target" position={Position.Left} className="bg-primary! w-2! h-2!" />
      <Handle type="source" position={Position.Right} className="bg-primary! w-2! h-2!" />

      {/* Table header */}
      <div className="px-3 py-2 border-b border-border bg-secondary/50 rounded-t-lg">
        <span className="text-sm font-semibold text-foreground font-mono">{table.name}</span>
      </div>

      {/* Columns */}
      <div className="divide-y divide-border/50">
        {table.columns.slice(0, 12).map((col) => (
          <div
            key={col.name}
            className="flex items-center justify-between px-3 py-1 text-sm hover:bg-muted/30"
          >
            <div className="flex items-center gap-1.5 min-w-0">
              {col.isPrimaryKey && (
                <span className="text-amber-600 text-sm font-bold">PK</span>
              )}
              {col.isForeignKey && (
                <span className="text-blue-600 text-sm font-bold">FK</span>
              )}
              <span className={`font-mono truncate ${col.isPrimaryKey ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                {col.name}
              </span>
            </div>
            <span className="text-sm text-muted-foreground/50 ml-2 shrink-0">{col.type}</span>
          </div>
        ))}
        {table.columns.length > 12 && (
          <div className="px-3 py-1 text-sm text-muted-foreground/50 text-center">
            +{table.columns.length - 12} more
          </div>
        )}
      </div>
    </div>
  );
}

const nodeTypes = { tableNode: TableNode };

export function SchemaDiagram({
  schema,
  onSelectTable,
}: {
  schema: string;
  onSelectTable: (name: string) => void;
}) {
  const [data, setData] = useState<DiagramData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hoveredTable, setHoveredTable] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const diagramRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    fetch(`/api/database/schema-diagram?schema=${encodeURIComponent(schema)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.error) setError(d.error);
        else setData(d);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [schema]);

  const storageKey = `diagram-positions-${schema}`;

  const loadPositions = useCallback((): Record<string, { x: number; y: number }> => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, [storageKey]);

  const savePositions = useCallback((nodes: Node[]) => {
    const positions: Record<string, { x: number; y: number }> = {};
    for (const n of nodes) {
      positions[n.id] = { x: n.position.x, y: n.position.y };
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(positions));
    } catch {
      // storage full or unavailable
    }
  }, [storageKey]);

  const { initialNodes, initialEdges } = useMemo(() => {
    if (!data) return { initialNodes: [], initialEdges: [] };

    const saved = loadPositions();

    // Auto-layout: grid arrangement (used as fallback)
    const cols = Math.ceil(Math.sqrt(data.tables.length));
    const colWidth = 380;
    const rowHeight = 320;

    const nodes: Node[] = data.tables.map((table, i) => ({
      id: table.name,
      type: "tableNode",
      position: saved[table.name] ?? {
        x: (i % cols) * colWidth + 50,
        y: Math.floor(i / cols) * rowHeight + 50,
      },
      data: { table, onSelect: onSelectTable, onHover: setHoveredTable },
    }));

    const edges: Edge[] = data.edges
      .filter((e) => data.tables.some((t) => t.name === e.target))
      .map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        type: "smoothstep",
        label: `${e.sourceColumn} -> ${e.targetColumn}`,
        labelStyle: { fontSize: 10, fill: "#666", fontWeight: 500 },
        labelBgStyle: { fill: "#fff", fillOpacity: 0.9 },
        labelBgPadding: [6, 3] as [number, number],
        style: { stroke: "#7f54b3", strokeWidth: 2 },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: "#7f54b3",
          width: 12,
          height: 12,
        },
        animated: false,
      }));

    return { initialNodes: nodes, initialEdges: edges };
  }, [data, onSelectTable, loadPositions]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update when data changes
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Compute related table set for hover highlighting
  const relatedTables = useMemo(() => {
    if (!hoveredTable || !data) return new Set<string>();
    const related = new Set<string>();
    related.add(hoveredTable);
    for (const edge of data.edges) {
      if (edge.source === hoveredTable) related.add(edge.target);
      if (edge.target === hoveredTable) related.add(edge.source);
    }
    return related;
  }, [hoveredTable, data]);

  // Apply opacity to nodes based on hover
  const styledNodes = useMemo(() => {
    if (!hoveredTable) return nodes;
    return nodes.map((n) => ({
      ...n,
      style: {
        ...n.style,
        opacity: relatedTables.has(n.id) ? 1 : 0.2,
        transition: "opacity 0.2s ease",
      },
    }));
  }, [nodes, hoveredTable, relatedTables]);

  // Apply opacity to edges based on hover
  const styledEdges = useMemo(() => {
    if (!hoveredTable) return edges;
    return edges.map((e) => ({
      ...e,
      style: {
        ...e.style,
        opacity: relatedTables.has(e.source) && relatedTables.has(e.target) ? 1 : 0.1,
        transition: "opacity 0.2s ease",
      },
    }));
  }, [edges, hoveredTable, relatedTables]);

  // Export handlers
  async function handleExportPng() {
    const el = diagramRef.current;
    if (!el) return;
    setExporting(true);
    try {
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(el, { quality: 1.0, backgroundColor: "#fff" });
      const link = document.createElement("a");
      link.download = `schema-${schema}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("PNG exported");
    } catch (err: any) {
      toast.error("Export failed", { description: err.message || "Unknown error" });
    } finally {
      setExporting(false);
    }
  }

  async function handleExportSvg() {
    const el = diagramRef.current;
    if (!el) return;
    setExporting(true);
    try {
      const { toSvg } = await import("html-to-image");
      const dataUrl = await toSvg(el);
      const link = document.createElement("a");
      link.download = `schema-${schema}.svg`;
      link.href = dataUrl;
      link.click();
      toast.success("SVG exported");
    } catch (err: any) {
      toast.error("Export failed", { description: err.message || "Unknown error" });
    } finally {
      setExporting(false);
    }
  }

  const handleNodesChange = useCallback(
    (changes: any) => {
      onNodesChange(changes);
      // Save after drag ends
      const hasDragStop = changes.some((c: any) => c.type === "position" && c.dragging === false);
      if (hasDragStop) {
        setNodes((current) => {
          savePositions(current);
          return current;
        });
      }
    },
    [onNodesChange, setNodes, savePositions]
  );

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-xs text-destructive">{error}</p>
      </div>
    );
  }

  if (!data || data.tables.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-muted-foreground">No tables in this schema</p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {/* Export toolbar */}
      <div className="flex items-center gap-2 px-4 py-1.5 border-b border-border/30 bg-card shrink-0">
        <span className="text-xs text-muted-foreground flex-1">
          {data.tables.length} {data.tables.length === 1 ? "table" : "tables"}, {data.edges.length} {data.edges.length === 1 ? "relation" : "relations"}
        </span>
        <Button size="sm" variant="outline" onClick={handleExportPng} disabled={exporting}>
          <Image className="h-3 w-3" />
          Export PNG
        </Button>
        <Button size="sm" variant="outline" onClick={handleExportSvg} disabled={exporting}>
          <Download className="h-3 w-3" />
          Export SVG
        </Button>
      </div>

      <div ref={diagramRef} className="flex-1 min-h-0">
        <ReactFlow
          nodes={styledNodes}
          edges={styledEdges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ maxZoom: 1 }}
          minZoom={0.1}
          maxZoom={2}
          defaultEdgeOptions={{
            type: "smoothstep",
            style: { stroke: "#7f54b3", strokeWidth: 2 },
            markerEnd: {
              type: MarkerType.ArrowClosed,
              color: "#7f54b3",
              width: 16,
              height: 16,
            },
          }}
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#ddd" gap={20} size={1} />
          <Controls
            className="bg-card! border-border! shadow-lg! [&>button]:bg-card! [&>button]:border-border! [&>button]:text-foreground! [&>button:hover]:bg-muted!"
          />
          <MiniMap
            className="bg-card! border-border!"
            nodeColor="#ccc"
            maskColor="rgba(255,255,255,0.7)"
          />
        </ReactFlow>
      </div>
    </div>
  );
}
